/**
 * 나라장터쇼핑몰 API 클라이언트 — backend /g2b/* 와 1:1.
 */

import type { ApiResponse, ApiResponseList } from './types';
import type { VolumeDiscount } from './g2b-discount';

const BASE = '/api';

export interface MasProductRow {
  id: number;
  category: string;       // '서버' | '디스크어레이' | ...
  fullName: string;
  g2bId: string | null;
  priceText: string;
  deliveryDays: string;
  endDate: string;
  note: string | null;
  sortOrder: number;
  hasImage: boolean;      // 제품 사진 보유 여부(식별번호 클릭 팝업)
}

/** 제품 사진 URL — 식별번호 클릭 팝업/미리보기 <img src>. bust=캐시무효화(교체 직후). */
export function masImageUrl(id: number, bust?: number | string): string {
  return `${BASE}/g2b/mas/${id}/image${bust != null ? `?v=${bust}` : ''}`;
}

/** 제품 사진 업로드/교체 (sales+admin). */
export async function uploadMasImage(id: number, file: File | Blob): Promise<void> {
  const fd = new FormData();
  fd.append('image', file, (file as File).name || 'product.png');
  const res = await fetch(`${BASE}/g2b/mas/${id}/image`, {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  const json = (await res.json()) as ApiResponse<{ id: number }>;
  if (!json.ok) throw new Error(json.error.message);
}

/** 제품 사진 삭제 (sales+admin). */
export async function deleteMasImage(id: number): Promise<void> {
  const res = await fetch(`${BASE}/g2b/mas/${id}/image`, {
    method: 'DELETE',
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<{ id: number }>;
  if (!json.ok) throw new Error(json.error.message);
}

export interface MasProductInput {
  category: string;
  fullName: string;
  g2bId?: string | null;
  priceText?: string;
  deliveryDays?: string;
  endDate?: string;
  note?: string | null;
  sortOrder?: number;
}

export async function listMas(): Promise<MasProductRow[]> {
  const res = await fetch(`${BASE}/g2b/mas`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponseList<MasProductRow>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}
export async function createMas(input: MasProductInput): Promise<number> {
  const res = await fetch(`${BASE}/g2b/mas`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const json = (await res.json()) as ApiResponse<{ id: number }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data.id;
}
export async function updateMas(id: number, input: Partial<MasProductInput>): Promise<void> {
  const res = await fetch(`${BASE}/g2b/mas/${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const json = (await res.json()) as ApiResponse<{ id: number }>;
  if (!json.ok) throw new Error(json.error.message);
}
export async function deleteMas(id: number): Promise<void> {
  const res = await fetch(`${BASE}/g2b/mas/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<{ id: number }>;
  if (!json.ok) throw new Error(json.error.message);
}

// ── 3자단가 (TPP) ──────────────────────────────────

export interface TppRow {
  id: number;
  ctype: '선택부품' | '주품목';
  cat: string;
  specText: string;
  g2bId: string;
  price: number;
  vendors: string[];
  note: string | null;
  sortOrder: number;
  hasImage: boolean;      // 제품 사진 보유 여부(식별번호 클릭 팝업) — 2026-07-29
}

/** 3자단가 제품 사진 URL — MAS 와 동일 규칙. bust=캐시무효화(교체 직후). */
export function tppImageUrl(id: number, bust?: number | string): string {
  return `${BASE}/g2b/tpp/${id}/image${bust != null ? `?v=${bust}` : ''}`;
}

/** 3자단가 제품 사진 업로드/교체 (sales+admin). */
export async function uploadTppImage(id: number, file: File | Blob): Promise<void> {
  const fd = new FormData();
  fd.append('image', file, (file as File).name || 'product.png');
  const res = await fetch(`${BASE}/g2b/tpp/${id}/image`, {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  const json = (await res.json()) as ApiResponse<{ id: number }>;
  if (!json.ok) throw new Error(json.error.message);
}

/** 3자단가 제품 사진 삭제 (sales+admin). */
export async function deleteTppImage(id: number): Promise<void> {
  const res = await fetch(`${BASE}/g2b/tpp/${id}/image`, {
    method: 'DELETE',
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<{ id: number }>;
  if (!json.ok) throw new Error(json.error.message);
}

export interface TppInput {
  ctype: '선택부품' | '주품목';
  cat: string;
  specText: string;
  g2bId: string;
  price: number;
  vendors: string[];
  note?: string | null;
  sortOrder?: number;
}

export async function listTpp(): Promise<TppRow[]> {
  const res = await fetch(`${BASE}/g2b/tpp`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponseList<TppRow>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}
export async function createTpp(input: TppInput): Promise<number> {
  const res = await fetch(`${BASE}/g2b/tpp`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const json = (await res.json()) as ApiResponse<{ id: number }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data.id;
}
export async function updateTpp(id: number, input: Partial<TppInput>): Promise<void> {
  const res = await fetch(`${BASE}/g2b/tpp/${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const json = (await res.json()) as ApiResponse<{ id: number }>;
  if (!json.ok) throw new Error(json.error.message);
}
export async function deleteTpp(id: number): Promise<void> {
  const res = await fetch(`${BASE}/g2b/tpp/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<{ id: number }>;
  if (!json.ok) throw new Error(json.error.message);
}

// ── 정산요율 (Rate) ─────────────────────────────────

export interface RateRow {
  id: number;
  kind: string;            // 'MAS' | '선택부품' | '주품목' | '사용자 정의' 등
  vendor: string;
  spec: string;
  price: number;
  fee: number | null;
  rate: number | null;
  /** 수수료(원)를 직접 적었나(v2.286.0) — 켜져 있으면 수수료가 기준, 요율은 서버가 거꾸로 계산 */
  feeFixed: boolean;
  note: string | null;
  /** 다량납품할인 기준 (v2.315.0) — 없으면 null · 정산서 작성 창이 이 표로 줄마다 할인율을 채운다 */
  volumeDiscount: VolumeDiscount | null;
  sortOrder: number;
}

export interface RateInput {
  kind: string;
  vendor: string;
  spec: string;
  price: number;
  fee?: number | null;
  rate?: number | null;
  feeFixed?: boolean;
  note?: string | null;
  /** null = 기준 지움 · 안 보내면 그대로 (v2.315.0) */
  volumeDiscount?: VolumeDiscount | null;
  sortOrder?: number;
}

export async function listRate(): Promise<RateRow[]> {
  const res = await fetch(`${BASE}/g2b/rate`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponseList<RateRow>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}
export async function createRate(input: RateInput): Promise<number> {
  const res = await fetch(`${BASE}/g2b/rate`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const json = (await res.json()) as ApiResponse<{ id: number }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data.id;
}
export async function updateRate(id: number, input: Partial<RateInput>): Promise<void> {
  const res = await fetch(`${BASE}/g2b/rate/${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const json = (await res.json()) as ApiResponse<{ id: number }>;
  if (!json.ok) throw new Error(json.error.message);
}
export async function deleteRate(id: number): Promise<void> {
  const res = await fetch(`${BASE}/g2b/rate/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<{ id: number }>;
  if (!json.ok) throw new Error(json.error.message);
}

// ── 문서 생성 (관급내역서 / 정산서) ────────────────────

export interface GwangeupItem {
  section: 'tpp' | 'mas';
  ctype: string | null;
  cat: string;
  vendor: string;
  productName: string;
  spec: string;
  unit: string;
  qty: number;
  unitPrice: number;
  g2bId: string;
  note: string;
}

export interface GwangeupPayload {
  suyocheo: string;
  procurementFeeRate: number;    // 소수 (0.0054 = 0.54%)
  procurementFeeLabel: string;
  items: GwangeupItem[];
  /** 넘기면 생성 파일이 그 영업건의 [기타 관련서류]로 자동 보관 (2026-07-28) */
  salesId?: string | null;
  /** 문서함에서 연 보관본이면 그 번호 — 새로 만들지 않고 고친다 (v2.230.0) */
  docId?: number | null;
}

export interface SettlementItem {
  g2bId: string;
  productName: string;
  spec: string;
  contractUnitPrice: number;
  qty: number;
  thirdVendor: string | null;   // 이 항목의 3자단가업체 (MAS면 null)
  rateThird: number | null;     // 3자단가업체 요율(소수)
  rateSM: number | null;        // 에스엠 요율
  rateProposer: number | null;  // 제안사 요율
  note: string;                 // 엑셀 비고 칸 (작성 창 입력칸은 v2.314.0 · 개발요청 #30)
  /** 이 줄의 다량납품할인율(소수 · 0.005 = 0.5%) · null = 할인 없음 (v2.315.0 · 줄마다) — 옛 보관본엔 칸이 없다 */
  discountRate?: number | null;
  /** 이 줄을 만든 정산요율 항목 번호 — 다시 열 때 할인 기준을 찾는다 (v2.315.0) */
  rateId?: number | null;
}

export interface SettlementPayload {
  title: string;
  companySM: string;
  companyProposer: string;
  items: SettlementItem[];
  /** v2.314.0 보관본에만 — 정산서 전체에 한 값이던 다량납품할인율(줄에 칸이 없을 때 그 줄에 쓴다) · v2.315.0 창은 보내지 않는다 */
  discountRate?: number | null;
  /** 넘기면 생성 파일이 그 영업건의 [기타 관련서류]로 자동 보관 (2026-07-28) */
  salesId?: string | null;
  /** 문서함에서 연 보관본이면 그 번호 — 새로 만들지 않고 고친다 (v2.230.0) */
  docId?: number | null;
}

/**
 * 서버에서 받은 xlsx blob 을 브라우저 다운로드로 저장. Content-Disposition 파일명 존중.
 * 돌려주는 값 = 문서함 보관 번호(작성 통로만 · v2.230.0) — 없으면 null.
 */
async function downloadXlsx(path: string, body: unknown, fallbackName: string, method: 'POST' | 'GET' = 'POST'): Promise<number | null> {
  const res = await fetch(`${BASE}${path}`, method === 'POST'
    ? { method, credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
    : { method, credentials: 'include' });
  if (!res.ok) {
    let msg = `문서 생성 실패 (HTTP ${res.status})`;
    try { const j = await res.json(); if (j?.error?.message) msg = j.error.message; } catch { /* noop */ }
    throw new Error(msg);
  }
  const blob = await res.blob();
  // 파일명 추출
  let filename = fallbackName;
  const cd = res.headers.get('Content-Disposition') || '';
  const star = cd.match(/filename\*=UTF-8''([^;]+)/i);
  if (star) filename = decodeURIComponent(star[1]);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  const id = Number(res.headers.get('X-G2b-Doc-Id') ?? '');
  return Number.isInteger(id) && id > 0 ? id : null;
}

/** 관급내역서 작성 — 내려받기 + 문서함 보관(+영업건 사본). 보관 번호를 돌려준다 */
export function downloadGwangeup(payload: GwangeupPayload): Promise<number | null> {
  return downloadXlsx('/g2b/gwangeup/download.xlsx', payload, '관급내역서.xlsx');
}

/** 정산서 작성 — 내려받기 + 문서함 보관(+영업건 사본). 보관 번호를 돌려준다 */
export function downloadSettlement(payload: SettlementPayload): Promise<number | null> {
  return downloadXlsx('/g2b/settlement/download.xlsx', payload, '정산서.xlsx');
}

/**
 * 👁 정산서 미리보기 (v2.292.0 · 개발요청 #10) — [문서작성]과 같은 엑셀을 **보관 없이** 받는다(내려받기도 안 한다).
 * 화면 미리보기 창이 그릴 수 있게 blob 과 파일 이름을 돌려준다.
 */
export async function previewSettlement(payload: SettlementPayload): Promise<{ blob: Blob; filename: string }> {
  const res = await fetch(`${BASE}/g2b/settlement/preview.xlsx`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  });
  if (!res.ok) {
    let msg = `미리보기 실패 (HTTP ${res.status})`;
    try { const j = await res.json(); if (j?.error?.message) msg = j.error.message; } catch { /* noop */ }
    throw new Error(msg);
  }
  const star = (res.headers.get('Content-Disposition') || '').match(/filename\*=UTF-8''([^;]+)/i);
  return { blob: await res.blob(), filename: star ? decodeURIComponent(star[1]) : '정산서.xlsx' };
}

// ── 문서함 보관본 (v2.230.0 · 개발 요청 #16) ─────────────────────────
export type G2bDocType = '관급내역서' | '정산서';

/** 작성 창의 영업건 칸 모양 그대로 (SalesSearchSelect selected) */
export interface G2bLinkedSales {
  salesId: string; endUser: string | null; proposalCompany: string | null; projectName: string | null; status: string;
}

export interface G2bDocRow {
  id: number;
  docType: G2bDocType;
  title: string;
  filename: string;
  totalAmount: number | null;
  linkedSalesId: string | null;
  /** 연동한 영업건이 지워졌으면 null (linkedSalesId 는 남는다) */
  linkedSales: G2bLinkedSales | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface G2bDocDetail extends G2bDocRow {
  /** 작성 창 입력값 — 관급내역서면 GwangeupPayload, 정산서면 SettlementPayload (영업건·문서번호 칸 없음) */
  payload: GwangeupPayload | SettlementPayload;
}

async function g2bJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { credentials: 'include', ...init });
  const json = (await res.json()) as ApiResponse<T>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

export function listG2bDocs(params: { type?: G2bDocType | ''; q?: string } = {}): Promise<G2bDocRow[]> {
  const qs = new URLSearchParams();
  if (params.type) qs.set('type', params.type);
  if (params.q?.trim()) qs.set('q', params.q.trim());
  return g2bJson(`/g2b/documents${qs.toString() ? `?${qs.toString()}` : ''}`);
}

export function getG2bDoc(id: number): Promise<G2bDocDetail> {
  return g2bJson(`/g2b/documents/${id}`);
}

export function downloadG2bDoc(id: number, fallbackName: string): Promise<number | null> {
  return downloadXlsx(`/g2b/documents/${id}/download.xlsx`, null, fallbackName, 'GET');
}

/** 영업건 연동·바꾸기(salesId) · 풀기(null) — 그 영업건 [기타 관련서류] 사본도 함께 맞춘다 */
export function linkG2bDoc(id: number, salesId: string | null): Promise<G2bDocRow> {
  return g2bJson(`/g2b/documents/${id}/link-sales`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ salesId }),
  });
}

export function deleteG2bDoc(id: number): Promise<{ id: number }> {
  return g2bJson(`/g2b/documents/${id}`, { method: 'DELETE' });
}

// ---------- BOM 원가산정 구성(기초데이터) 영구 저장 ----------
// items 는 G2B.tsx 의 BomItem[] 형태. api 계층에선 unknown[] 로 두고 화면에서 캐스팅.
export interface BomTemplateRow {
  targetKind: 'mas' | 'tpp';
  targetKey: string;
  items: unknown[];
}

export async function listBom(): Promise<BomTemplateRow[]> {
  const res = await fetch(`${BASE}/g2b/bom`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponseList<BomTemplateRow>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

export async function saveBom(
  targetKind: 'mas' | 'tpp',
  targetKey: string,
  items: unknown[],
): Promise<void> {
  const res = await fetch(`${BASE}/g2b/bom`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ targetKind, targetKey, items }),
  });
  const json = (await res.json()) as ApiResponse<unknown>;
  if (!json.ok) throw new Error(json.error.message);
}
